import React, { useState } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import {
  Users,
  Briefcase,
  ChevronRight,
  LayoutDashboard,
  ClipboardList,
  FileText,
  Clock,
  Bot,
  BookOpen,
  MessageSquare,
  Star,
  Shield,
  Globe,
  UserCheck,
  CreditCard,
  Settings,
  Eye,
  X,
  ArrowLeft,
  Maximize2,
  Lock,
  Unlock,
  ToggleLeft,
  ToggleRight,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';

type Portal = 'client' | 'consultant';
type PlanMode = 'pro' | 'free';

interface PortalLink {
  label: string;
  href: string;
  icon: React.ElementType;
  badge?: string;
}

const CLIENT_LINKS: PortalLink[] = [
  { label: 'Dashboard',            href: '/workday-client/dashboard',            icon: LayoutDashboard },
  { label: 'Talent Pool',          href: '/workday-client/talent',               icon: Users,         badge: 'Pro' },
  { label: 'Post Jobs',            href: '/workday-client/jobs',                 icon: ClipboardList, badge: 'Pro' },
  { label: 'Interview Requests',   href: '/workday-client/interview-requests',   icon: UserCheck },
  { label: 'Contracts & Hires',    href: '/workday-client/contracts',            icon: FileText },
  { label: 'Approve Timesheets',   href: '/workday-client/timesheets',           icon: Clock },
  { label: 'Consultant Documents', href: '/workday-client/consultant-documents', icon: BookOpen },
  { label: 'Community',            href: '/workday-client/community',            icon: Globe },
  { label: 'AI Assistant',         href: '/workday-client/ai',                   icon: Bot,           badge: 'Pro' },
  { label: 'Invite Team Members',  href: '/workday-client/invite-team',          icon: Users,         badge: 'Pro' },
  { label: 'Billing',              href: '/workday-client/billing',              icon: CreditCard },
  { label: 'Upgrade to Pro',       href: '/workday-client/upgrade',              icon: Star },
  { label: 'Settings',             href: '/workday-client/settings',             icon: Settings },
];

const CONSULTANT_LINKS: PortalLink[] = [
  { label: 'Dashboard',    href: '/consultant/dashboard',    icon: LayoutDashboard },
  { label: 'Opportunities',href: '/consultant/opportunities', icon: Briefcase },
  { label: 'Applications', href: '/consultant/applications', icon: MessageSquare },
  { label: 'Assignments',  href: '/consultant/assignments',  icon: ClipboardList },
  { label: 'Timesheet',    href: '/consultant/timesheet',    icon: Clock },
  { label: 'Community',    href: '/consultant/community',    icon: Globe },
  { label: 'AI Assistant', href: '/consultant/ai',           icon: Bot },
  { label: 'Resources',    href: '/consultant/resources',    icon: BookOpen },
  { label: 'Profile',      href: '/consultant/profile',      icon: UserCheck },
  { label: 'View Profile', href: '/consultant/view-profile', icon: Eye },
  { label: 'Settings',     href: '/consultant/settings',     icon: Settings },
];

const PRO_FEATURES = [
  'Full consultant contact reveal (name, email, phone)',
  'Resume access for all applicants',
  'Pipeline & Tracking — move candidates between stages',
  'Candidate messaging',
  'Career page / job requisition URL field',
  'Pro Posting toggle (reveal company to consultants)',
  '3 active job posting seats',
  'AI Assistant',
  'Invite Team Members',
];

const FREE_FEATURES = [
  'View consultant profiles (anonymized)',
  'Pipeline read-only view',
  'Post job requisitions (unlimited)',
  'Interview requests',
  'Contracts & hires',
  'Timesheet approvals',
  'Consultant documents',
  'Community',
  'Billing & upgrade',
];

interface PreviewState {
  url: string;
  label: string;
  portal: Portal;
}

function PortalLinkList({
  portal,
  links,
  title,
  subtitle,
  accent,
  headerBg,
  icon: Icon,
  onSelect,
  planMode,
}: {
  portal: Portal;
  links: PortalLink[];
  title: string;
  subtitle: string;
  accent: string;
  headerBg: string;
  icon: React.ElementType;
  onSelect: (link: PortalLink) => void;
  planMode: PlanMode;
}) {
  const [hovered, setHovered] = useState<string | null>(null);

  return (
    <div className="flex flex-col bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden h-full">
      <div className={`px-5 py-4 ${headerBg} border-b border-gray-100`}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: accent + '22' }}>
            <Icon className="w-4.5 h-4.5" style={{ color: accent }} />
          </div>
          <div>
            <h2 className="text-sm font-bold text-gray-900">{title}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
          </div>
        </div>
        {portal === 'client' && (
          <div className={`mt-3 flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold ${planMode === 'pro' ? 'bg-[#40E0D0]/15 text-[#0ea5e9] border border-[#40E0D0]/30' : 'bg-gray-100 text-gray-600 border border-gray-200'}`}>
            {planMode === 'pro' ? <Unlock className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
            Testing as: {planMode === 'pro' ? 'Pro Plan' : 'Free Plan'}
          </div>
        )}
        <button
          onClick={() => onSelect(links[0])}
          className="mt-3 w-full flex items-center justify-center gap-2 py-2 px-4 rounded-lg text-sm font-semibold text-white transition-all active:scale-95"
          style={{ background: accent }}
        >
          <Eye className="w-4 h-4" />
          Open {title}
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-3">
        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-2 px-1">All Pages</p>
        <div className="space-y-0.5">
          {links.map((link) => {
            const LinkIcon = link.icon;
            const isHov = hovered === link.href;
            return (
              <button
                key={link.href}
                onClick={() => onSelect(link)}
                onMouseEnter={() => setHovered(link.href)}
                onMouseLeave={() => setHovered(null)}
                className={`group w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left transition-all ${isHov ? 'bg-gray-50' : ''}`}
              >
                <div
                  className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 transition-colors"
                  style={{ background: isHov ? accent + '1a' : '#f3f4f6' }}
                >
                  <LinkIcon className="w-3 h-3" style={{ color: isHov ? accent : '#9ca3af' }} />
                </div>
                <span className="flex-1 text-sm text-gray-700 font-medium">{link.label}</span>
                {link.badge && (
                  <span
                    className="text-[9px] font-bold px-1.5 py-0.5 rounded-full"
                    style={{ background: accent + '18', color: accent }}
                  >
                    {link.badge}
                  </span>
                )}
                <ChevronRight className="w-3 h-3 flex-shrink-0" style={{ color: isHov ? accent : '#e5e7eb' }} />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function ATSTesting() {
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [planMode, setPlanMode] = useState<PlanMode>('pro');

  const accentFor = (p: Portal) => p === 'client' ? '#0ea5e9' : '#40E0D0';

  const linkWithPlan = (link: PortalLink, portal: Portal, plan?: PlanMode): PortalLink => {
    if (portal !== 'client') return link;
    const effectivePlan = plan ?? planMode;
    const sep = link.href.includes('?') ? '&' : '?';
    return { ...link, href: `${link.href}${sep}adminPlan=${effectivePlan}` };
  };

  const handleSelect = (portal: Portal) => (link: PortalLink) => {
    const enriched = linkWithPlan(link, portal);
    setPreview({ url: enriched.href, label: link.label, portal });
  };

  // When planMode toggles while a client preview is open, reload the iframe with the updated plan param
  const handlePlanToggle = () => {
    const next: PlanMode = planMode === 'pro' ? 'free' : 'pro';
    setPlanMode(next);
    if (preview && preview.portal === 'client') {
      // Replace the adminPlan param in the current URL with the new plan
      const baseHref = preview.url.replace(/([?&])adminPlan=[^&]*/g, '');
      const sep = baseHref.includes('?') ? '&' : '?';
      setPreview({ ...preview, url: `${baseHref}${sep}adminPlan=${next}` });
    }
  };

  return (
    <AdminLayout>
      {/* Full-screen iframe overlay */}
      {preview && (
        <div className="fixed inset-0 z-50 flex flex-col bg-white" style={{ top: 0, left: 0 }}>
          {/* Toolbar */}
          <div
            className="flex items-center gap-3 px-4 py-2.5 border-b border-gray-200 flex-shrink-0"
            style={{ background: accentFor(preview.portal) + '0d' }}
          >
            <button
              onClick={() => setPreview(null)}
              className="flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors px-2 py-1 rounded-lg hover:bg-white"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to ATS Testing
            </button>
            <div className="w-px h-4 bg-gray-300" />
            <span className="text-xs text-gray-400">Previewing:</span>
            <span className="text-sm font-semibold text-gray-800">{preview.label}</span>
            <code className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">{preview.url}</code>
            <div className="flex-1" />
            {preview.portal === 'client' && (
              <button
                onClick={handlePlanToggle}
                className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-lg border transition-all hover:scale-[1.03] ${planMode === 'pro' ? 'bg-[#40E0D0]/15 text-[#0ea5e9] border-[#40E0D0]/40 hover:bg-[#40E0D0]/25' : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200'}`}
                title="Click to toggle plan mode"
              >
                {planMode === 'pro' ? <Unlock className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                {planMode === 'pro' ? 'Pro Plan' : 'Free Plan'}
                <span className="opacity-50 text-[10px]">(click to toggle)</span>
              </button>
            )}
            <div
              className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-lg"
              style={{ background: accentFor(preview.portal) + '18', color: accentFor(preview.portal) }}
            >
              <Shield className="w-3 h-3" />
              Admin Preview
            </div>
            {/* Page list quick-switcher */}
            <div className="relative group">
              <button className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 px-2 py-1 rounded-lg hover:bg-white transition-colors border border-gray-200">
                <Maximize2 className="w-3.5 h-3.5" />
                Switch Page
              </button>
              <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-10 hidden group-hover:block">
                {(preview.portal === 'client' ? CLIENT_LINKS : CONSULTANT_LINKS).map((l) => {
                  const LI = l.icon;
                  const enriched = linkWithPlan(l, preview.portal);
                  return (
                    <button
                      key={l.href}
                      onClick={() => setPreview({ url: enriched.href, label: l.label, portal: preview.portal })}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                    >
                      <LI className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                      <span className="flex-1">{l.label}</span>
                      {l.badge && (
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-600">{l.badge}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
            <button
              onClick={() => setPreview(null)}
              className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-700"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* iframe */}
          <iframe
            key={preview.url}
            src={preview.url}
            className="flex-1 w-full border-0"
            title={preview.label}
          />
        </div>
      )}

      {/* Main page */}
      <div className="p-6 max-w-5xl mx-auto">
        <div className="mb-7">
          <div className="flex items-center gap-3 mb-1.5">
            <div className="w-9 h-9 rounded-xl bg-[#40E0D0] bg-opacity-15 flex items-center justify-center">
              <Shield className="w-5 h-5 text-[#40E0D0]" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">PerplexityATS Testing</h1>
          </div>
          <p className="text-sm text-gray-500 ml-12">
            Admin-only portal access for QA and testing. Pages load inside this window using your admin session — no separate login required.
          </p>
          <div className="ml-12 mt-3 inline-flex items-center gap-1.5 bg-amber-50 border border-amber-200 text-amber-700 text-xs font-medium px-3 py-1.5 rounded-lg">
            <Shield className="w-3.5 h-3.5" />
            Admin session bypass active — visible to admin accounts only
          </div>
        </div>

        {/* Plan mode toggle for Client portal testing */}
        <div className="mb-6 rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
            <div>
              <p className="text-sm font-bold text-gray-900">Client Portal — Plan Testing Mode</p>
              <p className="text-xs text-gray-500 mt-0.5">Toggle between Pro and Free to test plan-gated features in the Client Application</p>
            </div>
            <button
              onClick={handlePlanToggle}
              className={`relative flex items-center gap-3 px-4 py-2.5 rounded-xl border-2 font-semibold text-sm transition-all ${planMode === 'pro' ? 'border-[#40E0D0] bg-[#40E0D0]/5 text-[#0ea5e9]' : 'border-gray-300 bg-gray-50 text-gray-600'}`}
            >
              {planMode === 'pro' ? (
                <><ToggleRight className="w-5 h-5" />Pro Plan Active</>
              ) : (
                <><ToggleLeft className="w-5 h-5" />Free Plan Active</>
              )}
            </button>
          </div>
          <div className="grid grid-cols-2 divide-x divide-gray-100">
            {/* Pro features */}
            <div className={`p-4 transition-colors ${planMode === 'pro' ? 'bg-[#40E0D0]/5' : 'opacity-50'}`}>
              <div className="flex items-center gap-2 mb-3">
                <Unlock className="w-4 h-4 text-[#40E0D0]" />
                <span className="text-xs font-bold text-gray-700 uppercase tracking-widest">Pro Plan Features</span>
                {planMode === 'pro' && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#40E0D0] text-black">ACTIVE</span>}
              </div>
              <div className="space-y-1.5">
                {PRO_FEATURES.map(f => (
                  <div key={f} className="flex items-start gap-2">
                    <CheckCircle className="w-3.5 h-3.5 text-[#40E0D0] flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-gray-600">{f}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Free features */}
            <div className={`p-4 transition-colors ${planMode === 'free' ? 'bg-gray-50' : 'opacity-50'}`}>
              <div className="flex items-center gap-2 mb-3">
                <Lock className="w-4 h-4 text-gray-400" />
                <span className="text-xs font-bold text-gray-700 uppercase tracking-widest">Free Plan Features</span>
                {planMode === 'free' && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-600 text-white">ACTIVE</span>}
              </div>
              <div className="space-y-1.5">
                {FREE_FEATURES.map(f => (
                  <div key={f} className="flex items-start gap-2">
                    <CheckCircle className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-gray-600">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" style={{ minHeight: '520px' }}>
          <PortalLinkList
            portal="client"
            links={CLIENT_LINKS}
            title="Client Application"
            subtitle={planMode === 'pro' ? 'Testing as Pro Plan — full feature access' : 'Testing as Free Plan — restricted access'}
            accent="#0ea5e9"
            headerBg="bg-sky-50"
            icon={Briefcase}
            onSelect={handleSelect('client')}
            planMode={planMode}
          />
          <PortalLinkList
            portal="consultant"
            links={CONSULTANT_LINKS}
            title="Consultant Application"
            subtitle="All features and functionality — full consultant portal"
            accent="#40E0D0"
            headerBg="bg-teal-50"
            icon={UserCheck}
            onSelect={handleSelect('consultant')}
            planMode={planMode}
          />
        </div>

        <p className="mt-5 text-xs text-gray-400 text-center">
          Pages load using your active admin session via the admin bypass in PrivateRoute. The <code className="bg-gray-100 px-1 rounded">?adminPlan=</code> param overrides plan detection for testing.
        </p>
      </div>
    </AdminLayout>
  );
}
